import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { tours } from "@/data/tours";
import { SeoHead } from "@/components/common/SeoHead";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { QuoteRequestDialog } from "@/components/common/QuoteRequestDialog";
import { AnimatedSection } from "@/components/common/AnimatedSection";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Star, Clock, MapPin, Check, ArrowLeft, CalendarDays, Users, ArrowRight } from "lucide-react";

export default function TourDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const tour = tours.find((t) => t.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!tour) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="pt-32 pb-20 text-center container-wide px-4">
          <h1 className="heading-lg text-foreground mb-4">Tour Not Found</h1>
          <p className="text-muted-foreground mb-8">The tour you're looking for doesn't exist.</p>
          <Button onClick={() => navigate("/tours")} className="gap-2">
            <ArrowLeft className="h-4 w-4" /> Back to Tours
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const otherTours = tours.filter((t) => t.id !== id).slice(0, 3);

  return (
    <div className="min-h-screen">
      <SeoHead
        title={tour.title}
        description={`${tour.title} — ${tour.duration} guided tour to ${tour.destination}. From $${tour.price.toLocaleString()} per person. Request a free quote from Tripsyfy.`}
        canonical={`https://tripsyfy.com/tours/${tour.id}`}
        ogImage={tour.image}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative h-[50vh] sm:h-[60vh] flex items-end">
        <img src={tour.heroImage || tour.image} alt={tour.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/30 to-transparent" />
        <div className="relative z-10 container-wide px-4 sm:px-6 lg:px-8 pb-10">
          <Link to="/tours" className="inline-flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground text-sm mb-4 transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to Tours
          </Link>
          <Badge className="bg-accent text-accent-foreground border-0 mb-3">{tour.region}</Badge>
          <h1 className="heading-xl text-primary-foreground mb-3">{tour.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-primary-foreground/80">
            <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {tour.destination}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" /> {tour.duration}</span>
            <span className="flex items-center gap-1.5"><Star className="h-4 w-4 fill-gold text-gold" /> {tour.rating} ({tour.reviews} reviews)</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-10">
              <AnimatedSection>
                <div>
                  <h2 className="heading-md text-foreground mb-4">About This Tour</h2>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{tour.description}</p>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.1}>
                <div>
                  <h2 className="heading-md text-foreground mb-6">Tour Highlights</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {tour.highlights.map((h, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
                        <div className="h-5 w-5 rounded-full bg-accent/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="h-3 w-3 text-accent" />
                        </div>
                        <span className="text-sm text-foreground">{h}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.15}>
                <div>
                  <h2 className="heading-md text-foreground mb-6">What's Included</h2>
                  <div className="space-y-2">
                    {tour.included.map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Check className="h-4 w-4 text-accent shrink-0" />
                        <span className="text-sm text-foreground">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection delay={0.2}>
                <div>
                  <h2 className="heading-md text-foreground mb-6">Day-by-Day Itinerary</h2>
                  <Accordion type="single" collapsible className="space-y-2">
                    {tour.itinerary.map((day) => (
                      <AccordionItem key={day.day} value={`day-${day.day}`} className="bg-card rounded-lg border border-border px-4">
                        <AccordionTrigger className="text-sm font-medium hover:no-underline">
                          <div className="flex items-center gap-3">
                            <span className="h-7 w-7 rounded-full bg-accent/10 text-accent text-xs font-bold flex items-center justify-center shrink-0">D{day.day}</span>
                            <span className="text-foreground">{day.title}</span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-muted-foreground pl-10">
                          {day.description}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </AnimatedSection>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1 lg:sticky lg:top-24 lg:self-start">
              <Card className="p-6 border-0 shadow-elevated">
                <div className="mb-6">
                  <p className="text-xs text-muted-foreground">Starting from</p>
                  <p className="text-3xl font-bold text-foreground">${tour.price.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">per person</p>
                </div>

                <div className="space-y-3 mb-6 text-sm">
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground flex items-center gap-2"><CalendarDays className="h-4 w-4" /> Duration</span>
                    <span className="font-medium text-foreground">{tour.duration}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground flex items-center gap-2"><MapPin className="h-4 w-4" /> Destination</span>
                    <span className="font-medium text-foreground">{tour.destination}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground flex items-center gap-2"><Users className="h-4 w-4" /> Group Size</span>
                    <span className="font-medium text-foreground">2-12 people</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-muted-foreground flex items-center gap-2"><Star className="h-4 w-4" /> Rating</span>
                    <span className="font-medium text-foreground flex items-center gap-1">{tour.rating} <Star className="h-3 w-3 fill-gold text-gold" /></span>
                  </div>
                </div>

                <Button className="w-full" onClick={() => setQuoteDialogOpen(true)}>
                  Request a Quote
                </Button>
                <p className="text-xs text-muted-foreground text-center mt-3">Free cancellation up to 48 hours before</p>
              </Card>
            </div>
          </div>

          {/* Other tours */}
          {otherTours.length > 0 && (
            <div className="mt-20">
              <AnimatedSection>
                <h2 className="heading-lg text-foreground mb-8">You Might Also Like</h2>
              </AnimatedSection>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {otherTours.map((t, i) => (
                  <AnimatedSection key={t.id} delay={i * 0.1}>
                    <Link to={`/tours/${t.id}`} className="group block">
                      <Card className="overflow-hidden border-0 shadow-card hover:shadow-card-hover transition-shadow duration-300">
                        <div className="relative aspect-[4/3] overflow-hidden">
                          <img src={t.image} alt={t.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                          <Badge className="absolute top-3 left-3 bg-accent text-accent-foreground border-0">{t.region}</Badge>
                        </div>
                        <div className="p-4">
                          <h3 className="font-heading text-lg font-semibold text-card-foreground group-hover:text-accent transition-colors">{t.title}</h3>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-sm text-muted-foreground">{t.duration}</span>
                            <span className="text-sm font-bold text-foreground">From ${t.price.toLocaleString()}</span>
                          </div>
                        </div>
                      </Card>
                    </Link>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <QuoteRequestDialog
        open={quoteDialogOpen}
        onOpenChange={setQuoteDialogOpen}
        quoteData={{ type: "tour", tourName: tour.title, destination: tour.destination, duration: tour.duration }}
      />
    </div>
  );
}
